import { useState, useEffect } from "react"
import { Card } from "primereact/card"
import { Button } from "primereact/button"
import { ProgressSpinner } from 'primereact/progressspinner'
import { Skeleton } from 'primereact/skeleton'

export default function DocumentoCard({ documento, onVerDocumento, onDescargarDocumento, loadingFiles }) {
  const [showPdfViewer, setShowPdfViewer] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [pdfError, setPdfError] = useState(false)

  // Definir variables antes de los hooks
  const isPdf = documento?.name?.toLowerCase().endsWith('.pdf') || false
  const isImage = documento?.name ? /\.(png|jpg|jpeg|gif|webp)$/i.test(documento.name.toLowerCase()) : false

  // Función para formatear el tamaño del archivo
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A'

    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))

    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }


  const handlePdfLoad = () => {
    setPdfLoading(false)
    setPdfError(false)
  }

  const handlePdfError = () => {
    setPdfLoading(false)
    setPdfError(true)
  }

  const PDFPreview = () => (
    <div className="w-full h-full relative">
      {pdfLoading && (
        <div className="absolute inset-0 flex align-items-center justify-content-center">
          <ProgressSpinner size="small" />
        </div>
      )}



      {pdfError ? (
        <div className="flex flex-column align-items-center justify-content-center h-full text-center p-2">
          <i className="pi pi-file-pdf text-4xl text-red-500 mb-2"></i>
          <span className="text-xs text-gray-600">PDF Preview</span>
        </div>
      ) : (
        <object
          data={`${documento.signed_url}#toolbar=0&navpanes=0&scrollbar=0&page=1&view=FitH`}
          type="application/pdf"
          width="100%"
          height="100%"
          onLoad={handlePdfLoad}
          onError={handlePdfError}
          style={{
            minHeight: '200px',
            border: 'none'
          }}
        >
          <div className="flex flex-column align-items-center justify-content-center h-full">
            <i className="pi pi-file-pdf text-4xl text-red-500 mb-2"></i>
            <span className="text-xs text-gray-600">PDF Document</span>
          </div>
        </object>
      )}
    </div>
  )

  const PDFThumbnail = () => (
    <div
      className="w-full h-full relative cursor-pointer hover:bg-gray-50 transition-colors"
      onClick={() => onVerDocumento(documento)}
    >
      <embed
        src={`${documento.signed_url}#toolbar=0&navpanes=0&scrollbar=0&page=1&view=Fit`}
        type="application/pdf"
        width="100%"
        height="100%"
        style={{
          border: 'none',
          pointerEvents: 'none', // Evita interacción directa con el PDF
          borderRadius: '4px'
        }}
      />

      {/* Overlay para click */}
      <div className="absolute inset-0 bg-transparent hover:bg-blue-50 hover:bg-opacity-20 transition-all">
        <div className="absolute bottom-2 right-2 bg-white bg-opacity-80 rounded p-1">
          <i className="pi pi-eye text-blue-500 text-xs"></i>
        </div>
      </div>
    </div>
  )

  const SimplePDFIcon = () => (
    <div
      className="flex flex-column align-items-center justify-content-center h-full cursor-pointer hover:bg-gray-50 transition-colors p-3"
      onClick={() => onVerDocumento(documento)}
    >
      <i className="pi pi-file-pdf text-6xl text-red-500 mb-2"></i>
      <span className="text-xs text-center text-gray-600 line-height-3">
        {documento.name.length > 20
          ? documento.name.substring(0, 20) + '...'
          : documento.name
        }
      </span>
      <span className="text-xs text-blue-500 mt-1">Click para ver</span>
    </div>
  )

  // Si está cargando archivos, mostrar skeleton
  if (loadingFiles) {
    return (
      <div className="col-12 md:col-12">
        <Card className="h-full">
          <div
            className="flex align-items-center justify-content-center mb-3 border-200 border-1 border-round"
            style={{ aspectRatio: "3/4", background: "var(--surface-100)" }}
          >
            <Skeleton width="100%" height="100%" />
          </div>

          <div className="flex align-items-center justify-content-between">
            <div className="flex flex-column w-full">
              <Skeleton width="70%" height="1rem" className="mb-2" />
              <Skeleton width="40%" height="0.8rem" />
            </div>
            <div className="flex gap-2">
              <Skeleton width="2.5rem" height="2rem" />
              <Skeleton width="2.5rem" height="2rem" />
            </div>
          </div>
        </Card>
      </div>
    )
  }

  // Si no hay documento, no renderizar nada
  if (!documento) return null

  return (
    <div className="col-12 md:col-12 ">
      <Card className="h-full">
        <div
          className="flex align-items-center justify-content-center mb-3 border-200 border-1 border-round overflow-hidden"
          style={{ aspectRatio: "3/4", background: "var(--surface-100)" }}
        >
          {isPdf ? (
            // Tres opciones para PDFs - descomenta la que prefieras:

            // Opción 1: Embed simple (recomendada)
            <PDFThumbnail />

            // Opción 2: Solo icono clickeable
            // <SimplePDFIcon />

            // Opción 3: Object con fallback
            // <PDFPreview />

          ) : isImage ? (
            // Para imágenes
            <img
              src={documento.signed_url}
              alt={documento.name}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              loading="lazy"
            />
          ) : (
            // Para otros tipos de archivo
            <div className="flex flex-column align-items-center justify-content-center h-full p-3">
              <i className="pi pi-file text-4xl text-blue-500 mb-2" />
              <span className="text-xs text-center text-gray-600">
                {documento.name}
              </span>
            </div>
          )}
        </div>

        <div className="flex align-items-center justify-content-between">
          <div className="flex flex-column">
            <span className="text-sm font-medium" title={documento.name}>
              {documento.name}
            </span>
            <span className="text-xs text-gray-500 mt-1">
              {formatFileSize(documento.size)}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              icon="pi pi-eye"
              outlined
              size="small"
              aria-label="Ver"
              tooltip="Ver documento"
              onClick={() => onVerDocumento(documento)}
            />
            {/*  <Button
               icon="pi pi-download"
               outlined
               size="small"
               aria-label="Descargar"
               tooltip="Descargar documento"
               onClick={() => onDescargarDocumento(documento.signed_url)}
             /> */}
          </div>
        </div>
      </Card>
    </div>
  )
}